import {
  Alert,
  Button,
  Checkbox,
  Col,
  message,
  Modal,
  Radio,
  RadioChangeEvent,
  Row,
  Space,
  Spin,
  Tag,
} from "antd";
import {StyledModalContentWrapper} from "../../../../../../components/calls/upcoming-calls/call-card/call-card.style";
import {
  useApplyScheduleToSessionApi,
  useDeleteUserScheduleApi,
  useFetchSessionsNotUsingSchedule,
  useFetchSessionsUsingSchedule,
  useGetUserSchedules,
  useRemoveSessionFromScheduleApi,
} from "../../../../../../react-query-hooks/schedule";
import {LoadingSpinner} from "../../../../../../components/icons/icons";
import {
  StyledApplyScheduleToSessionButtonWrapper,
  StyledApplyScheduleToSessionInfoBlock,
} from "./styles";
import {ClockCircleOutlined} from "@ant-design/icons";
import {useState} from "react";
import {CheckboxValueType} from "antd/lib/checkbox/Group";
import {CheckboxChangeEvent} from "antd/es/checkbox";
import {useSessions} from "../../../../../../features/sessions";
import {Link} from "react-router-dom";
import {useUserScheduleContext} from "../../context/user-schedule";
import {StyledServiceButtonWrapper} from "../../../../../../styles/onboarding/onboarding";

export type ApplyScheduleToSessionFormModalProps = {
  handleApplyScheduleToSessionModalOk(): void;
  handleCloseApplyScheduleToSessionModal(): void;
  isApplyScheduleToSessionModalOpen: boolean;
  scheduleId: string;
  scheduleName: string;
};

export function ApplyScheduleToSessionFormModal({
  handleCloseApplyScheduleToSessionModal,
  isApplyScheduleToSessionModalOpen,
  scheduleId,
  scheduleName,
}: ApplyScheduleToSessionFormModalProps) {
  const getSessionsQuery = useSessions({page: 1, perPage: 1});

  const sessionsData = getSessionsQuery.data?.data || [];

  function closeModal() {
    handleCloseApplyScheduleToSessionModal();
  }

  return (
    <Modal
      visible={isApplyScheduleToSessionModalOpen}
      title={"Apply schedule to session(s)"}
      onOk={closeModal}
      onCancel={closeModal}
      footer={null}
      centered
    >
      <StyledModalContentWrapper>
        {sessionsData.length > 0 ? (
          <>
            <SessionsUsingScheduleListView
              scheduleId={scheduleId}
              scheduleName={scheduleName}
              closeApplyScheduleModal={closeModal}
            />
            <SessionsNotUsingScheduleListView
              scheduleId={scheduleId}
              scheduleName={scheduleName}
            />
          </>
        ) : (
          <>
            <p>
              You do not have any session yet. Create a new session to apply schedule{" "}
              <Link to="/new-offering?selectOption=event">
                <span style={{textDecoration: "underline", fontWeight: 600}}>
                  Create a new session
                </span>
              </Link>
            </p>
          </>
        )}
        {/* <Space size={16} direction="vertical" style={{display: "flex"}}> */}

        {/* </Space> */}
      </StyledModalContentWrapper>
    </Modal>
  );
}

export function SessionsNotUsingScheduleListView({
  scheduleId,
  scheduleName,
}: {
  scheduleId: ApplyScheduleToSessionFormModalProps["scheduleId"];
  scheduleName: ApplyScheduleToSessionFormModalProps["scheduleName"];
}) {
  const [checkedValues, setCheckedValues] = useState<CheckboxValueType[]>([]);
  const fetchSessionsNotUsingSchedule = useFetchSessionsNotUsingSchedule(scheduleId);
  const applyScheduleToSessionApi = useApplyScheduleToSessionApi();

  if (fetchSessionsNotUsingSchedule.isLoading) {
    return (
      <div style={{display: "flex", justifyContent: "center", width: "100%"}}>
        <Spin indicator={LoadingSpinner} />
      </div>
    );
  }
  if (fetchSessionsNotUsingSchedule.isError) {
    return <Alert message={fetchSessionsNotUsingSchedule.error.message} type="error" />;
  }

  return (
    <div style={{marginTop: "1.6rem"}}>
      {fetchSessionsNotUsingSchedule.data.sessions.length > 0 ? (
        <Space size={8} direction="vertical" style={{display: "flex"}}>
          <StyledApplyScheduleToSessionInfoBlock>
            <Space size={10} align="center">
              {/* <ClockCircleOutlined color="#22AA9F" /> */}
              <p>Sessions not using {scheduleName}</p>
            </Space>
          </StyledApplyScheduleToSessionInfoBlock>
          <div>
            <Checkbox.Group
              onChange={(e) => {
                console.log({checkboxE: e});
                setCheckedValues(e);
                if (e.length > 0) {
                  const lastValue = Number(e[e.length - 1]);
                  applyScheduleToSessionApi.mutate(
                    {
                      scheduleId,
                      sessionId: [lastValue],
                    },
                    {
                      onSuccess: () => {
                        fetchSessionsNotUsingSchedule.refetch();
                        setCheckedValues([]);
                      },
                      onError: () => setCheckedValues([]),
                    }
                  );
                }
              }}
              disabled={
                fetchSessionsNotUsingSchedule.isLoading ||
                fetchSessionsNotUsingSchedule.isFetching
              }
              value={checkedValues}
            >
              <Space size={8} direction="vertical" style={{display: "flex"}}>
                {fetchSessionsNotUsingSchedule.data.sessions.map((s) => (
                  <Checkbox key={s.id} value={s.id}>
                    {s.name}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </div>
        </Space>
      ) : null}
    </div>
  );
}

export function SessionsUsingScheduleListView({
  scheduleId,
  scheduleName,
  closeApplyScheduleModal,
}: {
  scheduleId: ApplyScheduleToSessionFormModalProps["scheduleId"];
  scheduleName: ApplyScheduleToSessionFormModalProps["scheduleName"];
  closeApplyScheduleModal(): void;
}) {
  const {dispatch} = useUserScheduleContext();
  const [isApplyToNewScheduleModalOpen, setIsApplyToNewScheduleModalOpen] =
    useState<boolean>(false);
  const [newScheduleToBeAppliedTo, setNewScheduleToBeAppliedTo] = useState<string>();
  const [sessionToRemove, setSessionToRemove] = useState<number>();

  const fetchSessionsUsingSchedule = useFetchSessionsUsingSchedule(scheduleId);
  const removeSessionFromScheduleApi = useRemoveSessionFromScheduleApi();
  const applyScheduleToSessionApi = useApplyScheduleToSessionApi();
  const getUserSchedulesQuery = useGetUserSchedules();

  if (fetchSessionsUsingSchedule.isLoading) {
    return (
      <div style={{display: "flex", justifyContent: "center", width: "100%"}}>
        <Spin indicator={LoadingSpinner} />
      </div>
    );
  }
  if (fetchSessionsUsingSchedule.isError) {
    return <Alert message={fetchSessionsUsingSchedule.error.message} type="error" />;
  }

  function closeApplyToNewScheduleModal() {
    setNewScheduleToBeAppliedTo(undefined);
    setSessionToRemove(undefined);
    setIsApplyToNewScheduleModalOpen(false);
  }
  function openApplyToNewScheduleModal() {
    setIsApplyToNewScheduleModalOpen(true);
  }

  function onCheckBoxChange(e: CheckboxChangeEvent) {
    console.log({changeSingleBox: e.target.value});
    setSessionToRemove(e.target.value);
    closeApplyScheduleModal();
    openApplyToNewScheduleModal();
  }

  const onChangeNewScheduleToBeAppliedToCheckbox = (e: RadioChangeEvent) => {
    console.log("radio checked", e.target.value);
    setNewScheduleToBeAppliedTo(e.target.value);
  };

  function onApplyToNewScheduleSubmit() {
    if (sessionToRemove && newScheduleToBeAppliedTo) {
      //remove from previous schedule
      removeSessionFromScheduleApi.mutate(
        {
          scheduleId,
          sessionId: sessionToRemove,
        },
        {
          onSuccess: () => {
            //apply to new schedule
            applyScheduleToSessionApi.mutate(
              {
                scheduleId: newScheduleToBeAppliedTo,
                sessionId: [sessionToRemove],
              },
              {
                onSuccess: () => {
                  closeApplyToNewScheduleModal();
                },
                // onError: () => setCheckedValues([]),
              }
            );
            fetchSessionsUsingSchedule.refetch();
          },
          // onError: () => setCheckedValues([]),
        }
      );
    }
  }

  function handleCreateNewSchedule() {
    dispatch({
      type: "toggle-create-user-schedule-modal",
      payload: true,
    });
    closeApplyToNewScheduleModal();
  }

  return (
    <div>
      {fetchSessionsUsingSchedule.data.sessions.length > 0 ? (
        <Space size={8} direction="vertical" style={{display: "flex"}}>
          <StyledApplyScheduleToSessionInfoBlock>
            <Space size={10} align="center">
              {/* <ClockCircleOutlined color="#22AA9F" /> */}
              <p>Sessions using {scheduleName}</p>
            </Space>
          </StyledApplyScheduleToSessionInfoBlock>
          <div>
            <Space size={8} direction="vertical" style={{display: "flex"}}>
              {fetchSessionsUsingSchedule.data.sessions.map((s) => (
                <div key={s.id}>
                  <Checkbox
                    disabled={Number(getUserSchedulesQuery.data?.schedules.length) <= 1}
                    value={s.id}
                    defaultChecked
                    onChange={onCheckBoxChange}
                    checked
                  >
                    {s.name}
                  </Checkbox>
                </div>
              ))}
            </Space>
          </div>
        </Space>
      ) : null}

      <div>
        <Modal
          visible={isApplyToNewScheduleModalOpen}
          title={
            <p style={{paddingRight: "1.6rem"}}>Select a new schedule for this session</p>
          }
          onOk={closeApplyToNewScheduleModal}
          onCancel={closeApplyToNewScheduleModal}
          footer={null}
          centered
        >
          <StyledModalContentWrapper>
            <Space size={16} direction="vertical" style={{display: "flex"}}>
              <p>
                This session must always be linked to a schedule. To remove it from the
                current one, choose another schedule below
              </p>
              {getUserSchedulesQuery.isLoading ? (
                <div style={{display: "flex", justifyContent: "center", width: "100%"}}>
                  <Spin indicator={LoadingSpinner} />
                </div>
              ) : getUserSchedulesQuery.isError ? (
                <Alert message={getUserSchedulesQuery.error.message} type="error" />
              ) : (
                <div>
                  <Radio.Group
                    onChange={onChangeNewScheduleToBeAppliedToCheckbox}
                    value={newScheduleToBeAppliedTo}
                  >
                    <Space direction="vertical">
                      {getUserSchedulesQuery.data.schedules
                        .filter((x) => x.id !== scheduleId)
                        .map((sch) => (
                          <Radio key={sch.id} value={sch.id}>
                            {sch.name}{" "}
                            {sch.isDefault && <Tag color="purple">Default</Tag>}
                          </Radio>
                        ))}
                    </Space>
                  </Radio.Group>
                </div>
              )}
              <StyledApplyScheduleToSessionButtonWrapper>
                <Button
                  className="secondary-btn"
                  size="small"
                  onClick={handleCreateNewSchedule}
                >
                  Create new schedule
                </Button>
                <Button
                  size="small"
                  type="primary"
                  onClick={onApplyToNewScheduleSubmit}
                  loading={
                    removeSessionFromScheduleApi.isLoading ||
                    applyScheduleToSessionApi.isLoading
                  }
                  disabled={!newScheduleToBeAppliedTo}
                >
                  Apply schedule
                </Button>
              </StyledApplyScheduleToSessionButtonWrapper>
            </Space>
          </StyledModalContentWrapper>
        </Modal>
      </div>
    </div>
  );
}

export function AssignSessionsToScheduleOnDelete({
  closeAssignSessionsToScheduleOnDeleteModal,
  isAssignSessionsToScheduleOnDeleteModalOpen,
  scheduleId,
  scheduleName,
}: {
  isAssignSessionsToScheduleOnDeleteModalOpen: boolean;
  closeAssignSessionsToScheduleOnDeleteModal(): void;
  scheduleId: ApplyScheduleToSessionFormModalProps["scheduleId"];
  scheduleName: ApplyScheduleToSessionFormModalProps["scheduleName"];
}) {
  const {dispatch} = useUserScheduleContext();
  const getUserSchedulesQuery = useGetUserSchedules();
  const deleteUserScheduleApi = useDeleteUserScheduleApi();
  const [newScheduleToBeAppliedTo, setNewScheduleToBeAppliedTo] = useState<string>();

  const onChangeNewScheduleToBeAppliedToCheckbox = (e: RadioChangeEvent) => {
    // console.log("radio checked", e.target.value);
    setNewScheduleToBeAppliedTo(e.target.value);
  };

  function onApplyToNewScheduleSubmit() {
    if (newScheduleToBeAppliedTo) {
      deleteUserScheduleApi.mutate(
        {
          scheduleId,
          newScheduleId: newScheduleToBeAppliedTo,
        },
        {
          onSuccess() {
            message.success(`${scheduleName} has been deleted successfully`);
          },
        }
      );
    }
  }

  function handleCreateNewSchedule() {
    setNewScheduleToBeAppliedTo(undefined);
    closeAssignSessionsToScheduleOnDeleteModal();
    dispatch({
      type: "toggle-create-user-schedule-modal",
      payload: true,
    });
  }
  return (
    <Modal
      visible={isAssignSessionsToScheduleOnDeleteModalOpen}
      title={
        <p style={{paddingRight: "1.6rem"}}>Select a new schedule for this session</p>
      }
      onOk={closeAssignSessionsToScheduleOnDeleteModal}
      onCancel={closeAssignSessionsToScheduleOnDeleteModal}
      footer={null}
      centered
    >
      <StyledModalContentWrapper>
        <Space size={16} direction="vertical" style={{display: "flex"}}>
          <p>
            This session must always be linked to a schedule. To remove it from the
            current one, choose another schedule below
          </p>
          {getUserSchedulesQuery.isLoading ? (
            <div style={{display: "flex", justifyContent: "center", width: "100%"}}>
              <Spin indicator={LoadingSpinner} />
            </div>
          ) : getUserSchedulesQuery.isError ? (
            <Alert message={getUserSchedulesQuery.error.message} type="error" />
          ) : (
            <div>
              <Radio.Group
                onChange={onChangeNewScheduleToBeAppliedToCheckbox}
                value={newScheduleToBeAppliedTo}
              >
                <Space direction="vertical">
                  {getUserSchedulesQuery.data.schedules
                    .filter((x) => x.id !== scheduleId)
                    .map((sch) => (
                      <Radio key={sch.id} value={sch.id}>
                        {sch.name} {sch.isDefault && <Tag color="purple">Default</Tag>}
                      </Radio>
                    ))}
                </Space>
              </Radio.Group>
            </div>
          )}
          <StyledApplyScheduleToSessionButtonWrapper>
            {/* <Space size={16} direction="horizontal" style={{display: "flex"}}> */}
            <Button
              className="secondary-btn"
              size="small"
              onClick={handleCreateNewSchedule}
            >
              Create new schedule
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={onApplyToNewScheduleSubmit}
              loading={deleteUserScheduleApi.isLoading}
              disabled={!newScheduleToBeAppliedTo}
            >
              Apply schedule
            </Button>
            {/* </Space> */}
          </StyledApplyScheduleToSessionButtonWrapper>
          {/* </div> */}
        </Space>
      </StyledModalContentWrapper>
    </Modal>
  );
}
